/**
 * ThemeProvider — remembers the user's dark/light preference in
 * localStorage and keeps the `.dark` or `.light` class on <html> in
 * sync. The app's CSS theme shim (index.css) handles the actual palette
 * swap when the class flips.
 *
 * Default: dark. We don't follow prefers-color-scheme automatically —
 * JackTrack is dark-first by brand, and sales reps who want it lighter
 * will click once and keep it there.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

const STORAGE_KEY = "jacktrack_theme";
const ThemeContext = createContext(null);

function readStored() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark") return v;
  } catch {
    /* iOS private mode / SSR — fall back to default */
  }
  return "dark";
}

function applyToDom(theme) {
  const root = document.documentElement;
  root.classList.remove("light", "dark");
  root.classList.add(theme);
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readStored);

  // Apply immediately on mount so there's no initial-flash of the wrong
  // theme (ThemeProvider is mounted at the root of the tree, before any
  // page renders).
  useEffect(() => {
    applyToDom(theme);
  }, [theme]);

  const setTheme = useCallback((next) => {
    setThemeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const toggle = useCallback(() => {
    setTheme(theme === "dark" ? "light" : "dark");
  }, [theme, setTheme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}
